import React, { useEffect, useState } from 'react';

import { Text, Title } from 'src/viewLayer/components/DesignSystem';
import EmailInput from 'src/viewLayer/components/shared/Inputs/EmailInput';
import { ForgotPasswordPageContainer, ForgotPasswordPageCard, ForgotPasswordPageFormContainer, ForgotPasswordPageTermsContainer } from 'src/viewLayer/components/App/index.styled';
import { InterpolatedLink } from 'src/viewLayer/components/shared/InterpolateLink/index.styled';
import NavBar from './NavBar';
import TrackableButton from 'src/viewLayer/components/shared/TrackableButton';
import Footer from 'src/viewLayer/components/pages/CardLayout/Footer';
import { t } from 'src/viewLayer/helpers/translations';
import urls from 'src/dataLayer/localData/urls';
import isEmailValid from 'src/viewLayer/components/flows/EmailLoginFlow/isEmailValid';
import useResetPasswordRequest from 'src/viewLayer/components/hooks/useResetPasswordRequest';
import { CaptchaPasswordReset } from 'src/viewLayer/components/CaptchaPasswordReset/CaptchaPasswordReset';
import { trackAuthenticationResetPassword } from 'src/dataLayer/remoteData/babbelEventStream';
import { showMagicLink } from 'src/viewLayer/navigation';
import { MagicLink } from  'src/viewLayer/components/shared/MagicLink/MagicLink';

const STATUS_IDLE = 'idle';
const STATUS_LOADING = 'loading';
const STATUS_SUCCESS = 'success';

export default function ForgotPasswordPage() {
    const [resetPasswordRequestStatus, setResetPasswordRequestStatus] = useState(STATUS_IDLE);
    const [captchaToken, setCaptchaToken] = useState();
    const [isButtonClicked, setIsButtonClicked] = useState(false);
    const [email, setEmail] = useState('');
    const [errorKey, setErrorKey] = useState('');
    const {
        makeResetPasswordRequest,
        isResetPasswordRequestInProgress
    } = useResetPasswordRequest({ captchaResponseToken: captchaToken, email });
    const isActionInProgress = isResetPasswordRequestInProgress || isButtonClicked;
    const params = new URLSearchParams(window.location.search);

    useEffect(() => {
        const emailParam = params.get('email');
        if (emailParam && isEmailValid(emailParam)) {
            setEmail(emailParam);
        }
        trackAuthenticationResetPassword({stage: 'token-request-page-shown'});
    }, []);

    useEffect(() => {
        if(resetPasswordRequestStatus === 'success'){
            trackAuthenticationResetPassword({stage: 'token-request-success-page-shown'});
        }
    }, [resetPasswordRequestStatus]);

    useEffect(() => {
        if (!captchaToken || !isButtonClicked) { return; }
        setIsButtonClicked(false);
        (async () => {
          setResetPasswordRequestStatus(STATUS_LOADING);
          const result = await makeResetPasswordRequest();
          if (result?.error) {
              setErrorKey(t(result.error));
              setResetPasswordRequestStatus(STATUS_IDLE);
              return;
          }
          setResetPasswordRequestStatus(STATUS_SUCCESS);
        })();
      }, [captchaToken, isButtonClicked, makeResetPasswordRequest]);

    const PrivacyPolicyLink = (
        <InterpolatedLink href={urls.privacyPolicyURL} weight="strong" target="_blank" size="xsmall">
            {t('forgot_password_page.terms_and_conditions.privacy_policy_link')}
        </InterpolatedLink>);
    const termsOfServiceLink = (
        <InterpolatedLink href={urls.termsOfServiceURL} weight="strong" target="_blank" size="xsmall">{
            t('forgot_password_page.terms_and_conditions.terms_service_link')}
        </InterpolatedLink>);

    const handleResetPassword = () => {
        setIsButtonClicked(true);
    };

    return (
        <>
            <NavBar />
            <ForgotPasswordPageContainer >
                <ForgotPasswordPageCard padding={{ xxsmall: [36, 8, 10], small: [60, 8, 24]}} gap={24}>
                    <Title size="small" textAlign="center">{t('forgot_password_page.title')}</Title>
                    {resetPasswordRequestStatus === 'success' ? (
                        <Text textAlign="center">{t('forgot_password_page.success_confirmation', { email })}</Text>
                    ) : (
                        <>
                            <Text textAlign="center">{t('forgot_password_page.description')}</Text>
                            <ForgotPasswordPageFormContainer gap={12}>
                                <EmailInput
                                    ariaDescribedby="message-uat-forgot-password-email-input-field"
                                    clearValue={() => setEmail('')}
                                    errorKey={errorKey}
                                    isFocused
                                    disabled={isActionInProgress}
                                    onChangeHandler={(e) => setEmail(e.target.value)}
                                    value={email}
                                />
                                <TrackableButton
                                    id="uat-forgot-password-button"
                                    dataTestid="forgot-password-page-submit-button"
                                    disabled={isActionInProgress || !isEmailValid(email)}
                                    title="Reset password button"
                                    trackingId="reset_password_submit"
                                    inProgress={isActionInProgress}
                                    fullWidth
                                    variant="primary"
                                    onClick={handleResetPassword}
                                >
                                    {t('forgot_password_page.button.reset_password')}
                                </TrackableButton>
                                { showMagicLink() && <MagicLink email={email} />}
                            </ForgotPasswordPageFormContainer>
                            <ForgotPasswordPageTermsContainer>
                                <Text textAlign="center" size="small">{t('forgot_password_page.terms_and_conditions.statement', { privacyPolicyLink: PrivacyPolicyLink, termsOfServiceLink: termsOfServiceLink })}</Text>
                            </ForgotPasswordPageTermsContainer>
                            <CaptchaPasswordReset setCaptchaToken={setCaptchaToken} shouldExecuteCaptcha={isButtonClicked} />
                        </>
                    )}
                </ForgotPasswordPageCard>
                <Footer />
            </ForgotPasswordPageContainer>
        </>
    );
};
